import React from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import AmountUSDRenderer from "components/common/ag-grid/AmountUSDRenderer";
import NumberRenderer from "components/common/ag-grid/NumberRenderer";
import DateFormatRenderer from "components/common/ag-grid/DateFormatRenderer";
import JobFormatRenderer from "components/app/crm/reports/components/JobFormatRenderer";
import { PersonRenderer, iconCell } from "components/common/ag-grid";
import { Avatar } from "components/common";
import { MdStorefront, MdPublic } from "react-icons/md";
import { LuMapPin, LuFileSignature, LuFilePlus2 } from "react-icons/lu";

// Company mark without a link — the combined/contract payloads carry only the
// customer name, so the cell gets the same initials-avatar anchor the project
// list uses, minus the navigation.
const CustomerNameCell = (p) =>
  p.value ? (
    <span className="tw-inline-flex tw-items-center tw-gap-2 tw-max-w-full">
      <Avatar name={p.value} alt={p.value} size="xs" className="tw-shrink-0" />
      <span className="tw-truncate">{p.value}</span>
    </span>
  ) : null;
CustomerNameCell.propTypes = { value: PropTypes.string };

// ── Shared cell renderers ─────────────────────────────────────────────────────

const ProjectLink = (p) => {
  const dealId = p.data?.deal_id;
  if (!dealId) return p.value || null;
  return (
    <Link to={`/crm/projects/${dealId}`} className="tw-text-blue-600 hover:tw-underline">
      {p.value}
    </Link>
  );
};
ProjectLink.propTypes = { data: PropTypes.object, value: PropTypes.any };

const QuoteLink = (p) => {
  const dealId = p.data?.deal_id;
  if (!p.value) return null;
  if (!dealId) return p.value;
  return (
    <Link to={`/crm/projects/${dealId}`} className="tw-text-yellow-600 hover:tw-underline tw-font-medium">
      {p.value}
    </Link>
  );
};
QuoteLink.propTypes = { data: PropTypes.object, value: PropTypes.any };

const ReleasedBadge = (p) => {
  const released = p.value;
  const cls = released
    ? "tw-bg-emerald-50 tw-border-emerald-300 tw-text-emerald-700"
    : "tw-bg-amber-50 tw-border-amber-300 tw-text-amber-700";
  return (
    <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-rounded-md tw-text-[11px] tw-leading-none tw-font-semibold tw-border tw-align-middle ${cls}`}>
      {released ? "Released" : "Pending"}
    </span>
  );
};
ReleasedBadge.propTypes = { value: PropTypes.bool };

const NoteCell = (p) => (p.value ? <span className="tw-text-rose-600">{p.value}</span> : null);
NoteCell.propTypes = { value: PropTypes.string };

// Combined tab: Contract vs Addendum — pill with a type icon, in the same
// visual language as the app's status badges (tinted fill, no border box).
const TypeBadge = (p) => {
  const isAddendum = p.value === "addendum";
  const cls = isAddendum
    ? "tw-bg-amber-50 tw-text-amber-700 tw-ring-1 tw-ring-inset tw-ring-amber-200"
    : "tw-bg-primary-50 tw-text-primary-700 tw-ring-1 tw-ring-inset tw-ring-primary-200";
  const Icon = isAddendum ? LuFilePlus2 : LuFileSignature;
  return (
    <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[11px] tw-leading-none tw-font-semibold tw-align-middle ${cls}`}>
      <Icon size={11} className="tw-shrink-0" />
      {isAddendum ? "Addendum" : "Contract"}
    </span>
  );
};
TypeBadge.propTypes = { value: PropTypes.string };

// ── Contracts tab columns (colId = ransack/DETAIL_SORT key) ───────────────────

export const contractColumns = [
  {
    headerName: "Job number", colId: "j_number", minWidth: 120, pinned: "left", flex: 0, width: 130,
    valueGetter: (p) => p.data?.j_number, cellRenderer: JobFormatRenderer
  },
  { headerName: "Project name", colId: "project_name", minWidth: 220, valueGetter: (p) => p.data?.project_name, cellRenderer: ProjectLink, tooltipValueGetter: (p) => p.value },
  { headerName: "Quote number", colId: "q_number", minWidth: 130, valueGetter: (p) => p.data?.q_number, cellRenderer: QuoteLink },
  { headerName: "Customer name", colId: "customer_name", minWidth: 200, sortable: false, valueGetter: (p) => p.data?.customer_name, cellRenderer: CustomerNameCell },
  { headerName: "Contract number", field: "contract_number", minWidth: 150, sortable: false },
  { headerName: "Contract date", colId: "contract_date", minWidth: 130, valueGetter: (p) => p.data?.contract_date, cellRenderer: DateFormatRenderer },
  { headerName: "Building", field: "building_name", minWidth: 160, sortable: false },
  { headerName: "Contract value", colId: "contract_value", minWidth: 150, valueGetter: (p) => p.data?.contract_value, cellRenderer: AmountUSDRenderer },
  { headerName: "Addendum value", field: "addendum_value", minWidth: 150, sortable: false, valueGetter: (p) => p.data?.addendum_value, cellRenderer: AmountUSDRenderer },
  { headerName: "Grand total", field: "grand_total_value", minWidth: 150, sortable: false, valueGetter: (p) => p.data?.grand_total_value, cellRenderer: AmountUSDRenderer },
  { headerName: "Weight (M.T)", colId: "contract_weight", minWidth: 140, valueGetter: (p) => p.data?.contract_weight, cellRenderer: NumberRenderer },
  { headerName: "Area (m²)", colId: "contract_area", minWidth: 120, valueGetter: (p) => p.data?.contract_area, cellRenderer: NumberRenderer },
  { headerName: "Signed by", field: "signed_by", minWidth: 120, sortable: false, cellRenderer: PersonRenderer },
  { headerName: "Signed date", colId: "signed_date", minWidth: 130, valueGetter: (p) => p.data?.signed_date, cellRenderer: DateFormatRenderer },
  { headerName: "Released", colId: "released", minWidth: 110, sortable: false, valueGetter: (p) => p.data?.released, cellRenderer: ReleasedBadge },
  { headerName: "Released date", colId: "released_date", minWidth: 140, valueGetter: (p) => p.data?.released_date, cellRenderer: DateFormatRenderer },
  { headerName: "Released by", field: "released_by", minWidth: 130, sortable: false, cellRenderer: PersonRenderer },
  { headerName: "Sales incharge", colId: "sales_incharge", minWidth: 130, valueGetter: (p) => p.data?.sales_incharge, cellRenderer: PersonRenderer },
  { headerName: "Sales unit", field: "unit_name", minWidth: 130, sortable: false, cellRenderer: iconCell(MdStorefront) },
  { headerName: "Region", field: "region", minWidth: 120, sortable: false, cellRenderer: iconCell(MdPublic) },
  { headerName: "Province", field: "pj_province", minWidth: 130, sortable: false, cellRenderer: iconCell(LuMapPin) },
  { headerName: "Country", field: "pj_country", minWidth: 130, sortable: false }
];

// ── Addendums tab columns (colId = ADDENDUM_SORT key) ─────────────────────────

export const addendumColumns = [
  { headerName: "Addendum number", colId: "addendum_number", minWidth: 150, pinned: "left", flex: 0, width: 160, valueGetter: (p) => p.data?.addendum_number },
  { headerName: "Job number", colId: "j_number", minWidth: 120, sortable: false, valueGetter: (p) => p.data?.j_number, cellRenderer: JobFormatRenderer },
  { headerName: "Project name", field: "project_name", minWidth: 220, sortable: false, valueGetter: (p) => p.data?.project_name, cellRenderer: ProjectLink, tooltipValueGetter: (p) => p.value },
  { headerName: "Quote number", field: "q_number", minWidth: 130, sortable: false, valueGetter: (p) => p.data?.q_number, cellRenderer: QuoteLink },
  { headerName: "Customer name", field: "customer_name", minWidth: 200, sortable: false, valueGetter: (p) => p.data?.customer_name },
  { headerName: "Contract number", field: "contract_number", minWidth: 150, sortable: false },
  { headerName: "Addendum date", colId: "addendum_date", minWidth: 140, valueGetter: (p) => p.data?.addendum_date, cellRenderer: DateFormatRenderer },
  { headerName: "Building", field: "building_name", minWidth: 160, sortable: false },
  { headerName: "Addendum value", colId: "addendum_value", minWidth: 150, valueGetter: (p) => p.data?.addendum_value, cellRenderer: AmountUSDRenderer },
  { headerName: "Weight (M.T)", colId: "addendum_weight", minWidth: 140, valueGetter: (p) => p.data?.addendum_weight, cellRenderer: NumberRenderer },
  { headerName: "Area (m²)", field: "addendum_area", minWidth: 120, sortable: false, valueGetter: (p) => p.data?.addendum_area, cellRenderer: NumberRenderer },
  { headerName: "Signed by", field: "signed_by", minWidth: 120, sortable: false, cellRenderer: PersonRenderer },
  { headerName: "Signed date", colId: "signed_date", minWidth: 130, valueGetter: (p) => p.data?.signed_date, cellRenderer: DateFormatRenderer },
  { headerName: "Released", field: "released", minWidth: 110, sortable: false, valueGetter: (p) => p.data?.released, cellRenderer: ReleasedBadge },
  { headerName: "Released date", colId: "released_date", minWidth: 140, valueGetter: (p) => p.data?.released_date, cellRenderer: DateFormatRenderer },
  { headerName: "Released by", field: "released_by", minWidth: 130, sortable: false, cellRenderer: PersonRenderer },
  { headerName: "Sales incharge", field: "sales_incharge", minWidth: 130, sortable: false, cellRenderer: PersonRenderer },
  { headerName: "Sales unit", field: "unit_name", minWidth: 130, sortable: false, cellRenderer: iconCell(MdStorefront) },
  { headerName: "Region", field: "region", minWidth: 120, sortable: false }
];

// ── Combined "All" tab columns (colId = all_records sort whitelist) ───────────
// Rows come from Crm::Dashboard::Contract.all_records (format_all_row): unified
// contract + addendum "signing events" keyed by their own date (display_date).

export const combinedColumns = [
  { headerName: "Type", colId: "record_type", minWidth: 110, pinned: "left", flex: 0, width: 120, sortable: false, valueGetter: (p) => p.data?.record_type, cellRenderer: TypeBadge },
  { headerName: "Job number", colId: "j_number", minWidth: 120, valueGetter: (p) => p.data?.j_number, cellRenderer: JobFormatRenderer },
  { headerName: "Number", field: "display_number", minWidth: 150, sortable: false },
  { headerName: "Project name", colId: "project_name", minWidth: 220, valueGetter: (p) => p.data?.project_name, cellRenderer: ProjectLink, tooltipValueGetter: (p) => p.value },
  { headerName: "Customer name", colId: "customer_name", minWidth: 200, valueGetter: (p) => p.data?.customer_name, cellRenderer: CustomerNameCell },
  { headerName: "Date", colId: "display_date", minWidth: 130, valueGetter: (p) => p.data?.display_date, cellRenderer: DateFormatRenderer },
  { headerName: "Value", colId: "display_value", minWidth: 150, valueGetter: (p) => p.data?.display_value, cellRenderer: AmountUSDRenderer },
  { headerName: "Weight (M.T)", colId: "display_weight", minWidth: 140, valueGetter: (p) => p.data?.display_weight, cellRenderer: NumberRenderer },
  { headerName: "Released by", colId: "released_by", minWidth: 130, valueGetter: (p) => p.data?.released_by, cellRenderer: PersonRenderer },
  { headerName: "Released date", colId: "released_date", minWidth: 140, valueGetter: (p) => p.data?.released_date, cellRenderer: DateFormatRenderer },
  { headerName: "Sales unit", field: "unit_name", minWidth: 130, sortable: false, cellRenderer: iconCell(MdStorefront) },
  { headerName: "Region", field: "region", minWidth: 120, sortable: false, cellRenderer: iconCell(MdPublic) },
  { headerName: "Sales incharge", field: "sales_incharge", minWidth: 130, sortable: false, cellRenderer: PersonRenderer },
  { headerName: "Building", colId: "building_name", minWidth: 160, sortable: false, valueGetter: (p) => p.data?.building_name },
  { headerName: "Province", field: "pj_province", minWidth: 130, sortable: false, cellRenderer: iconCell(LuMapPin) },
  { headerName: "Country", field: "pj_country", minWidth: 130, sortable: false }
];

export { NoteCell };
